<template>
  <Teleport to="body">
    <transition name="fade">
      <div
          v-if="isVisible"
          ref="popover"
          class="reader-analysis-popover fixed z-[9999] bg-white rounded-lg shadow-lg max-w-sm"
          :style="popoverStyle"
      >
        <button @click="close"
                class="absolute -top-3 -right-3 bg-white rounded-full shadow-md text-grey-400 hover:text-grey-700 text-sm w-7 h-7 flex items-center justify-center z-20 transition-colors">
          <i class="hi hi-close-light"></i>
        </button>

        <div class="popover-content max-h-[60vh] overflow-y-auto">
          <reader-annotation-analysis
              v-if="annotationanalysis && annotationanalysis.length > 0"
              :annotationanalysis="annotationanalysis"
          ></reader-annotation-analysis>

          <reader-vocab-analysis
              :wordanalysis="wordanalysis"
              :parent-context="vocabContext"
          ></reader-vocab-analysis>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import {EventBus} from "../../../hermeneus.eventbus/eventbus";
import ReaderAnnotationAnalysis from "./ReaderAnnotationAnalysis.vue";
import ReaderVocabAnalysis from "./ReaderVocabAnalysis/ReaderVocabAnalysis.vue";

export default {
  name: "reader-analysis-popover",
  props: ['wordanalysis', 'annotationanalysis', 'clickedWordElement', 'wordAnalysisLoading', 'wordAnalysisShowMorpho', 'readerConfig'],
  components: {
    'reader-annotation-analysis': ReaderAnnotationAnalysis,
    'reader-vocab-analysis': ReaderVocabAnalysis,
  },
  data () {
    return {
      posX: 0,
      posY: 0,
      /**
       * Kindkomponenten (ReaderVocabAnalysis) greifen auf $parent.WordAnalysisLoading zu
       */
      WordAnalysisLoading: this.wordAnalysisLoading,
    }
  },
  computed: {
    isVisible () {
      return this.wordanalysis !== false ||
          (this.annotationanalysis && this.annotationanalysis.length > 0);
    },
    popoverStyle () {
      return {
        left: this.posX + 'px',
        top: this.posY + 'px',
      };
    },
    /**
     * Kontextobjekt, das ReaderVocabAnalysis als parentContext-Prop erhält.
     * Ersetzt die $parent-Zugriffe im Popover-Modus.
     */
    vocabContext () {
      return {
        data: { config: this.readerConfig || {} },
        WordAnalysisLoading: this.WordAnalysisLoading,
        WordAnalysisShowMorpho: this.wordAnalysisShowMorpho,
        hasVocabAndAnnotationAnalysis: (this.annotationanalysis !== false && this.annotationanalysis?.length > 0) && (this.wordanalysis !== false),
      };
    },
  },
  watch: {
    wordAnalysisLoading (val) {
      this.WordAnalysisLoading = val;
    },
    clickedWordElement (newEl) {
      if (newEl) {
        this.$nextTick(() => this.updatePosition(newEl));
      }
    },
    wordanalysis () {
      if (this.clickedWordElement) {
        this.$nextTick(() => this.updatePosition(this.clickedWordElement));
      }
    },
    annotationanalysis () {
      if (this.clickedWordElement) {
        this.$nextTick(() => this.updatePosition(this.clickedWordElement));
      }
    },
  },
  methods: {
    updatePosition (wordEl) {
      const rect = wordEl.getBoundingClientRect();
      const popoverEl = this.$refs.popover;
      const MARGIN = 12;
      const popoverWidth = popoverEl ? popoverEl.offsetWidth : 384;
      const popoverHeight = popoverEl ? popoverEl.offsetHeight : 300;
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      // X: Linksbündig mit dem Wort, im Viewport geclampt
      let x = rect.left;
      if (x + popoverWidth > viewportW - MARGIN) {
        x = viewportW - popoverWidth - MARGIN;
      }
      if (x < MARGIN) x = MARGIN;

      // Y: Unterhalb des Wortes bevorzugt, bei Platzmangel oberhalb
      let y = rect.bottom + MARGIN;
      if (y + popoverHeight > viewportH - MARGIN) {
        // Nicht genug Platz unten - versuche oben
        y = rect.top - popoverHeight - MARGIN;
      }
      // Sicherstellen, dass das Popover immer im sichtbaren Bereich bleibt
      y = Math.max(MARGIN, Math.min(y, viewportH - popoverHeight - MARGIN));

      this.posX = x;
      this.posY = y;
    },
    close () {
      EventBus.$emit('setWordAnalysis', false);
      EventBus.$emit('setAnnotationAnalysis', []);
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar verstecken, Zeilenabstand erhöhen */
.popover-content {
  scrollbar-width: none;
  -ms-overflow-style: none;
  line-height: 1.6;
}
.popover-content::-webkit-scrollbar {
  display: none;
}

/* Annotation-Header im Popover ausblenden (eigener Close-Button + Icon) */
:deep(.reader__workspace__analysis-header) {
  display: none;
}

/* Annotation-Container: linker roter Akzentstreifen statt 4px Top-Border */
:deep(#reader__workspace__analysis--annotation) {
  border: none !important;
  border-left: 4px solid #ed6b6b !important;
  border-radius: 0 !important;
  margin: 0;
}

/* Vocab-Header im Popover ausblenden (eigener Close-Button + Icon) */
:deep(.reader__analysis-vocab__header) {
  display: none;
}

/* Vocab-Container: linker grüner Akzentstreifen statt 4px Top-Border */
:deep(#reader__workspace__analysis--vocab) {
  border: none !important;
  border-left: 4px solid #326470 !important;
  border-radius: 0 !important;
  margin: 0;
}

/* Kompakteres Padding in den Sektionen */
:deep(.reader__workspace__analysis-body),
:deep(.reader__analysis-vocab__body) {
  padding: 6px 10px;
}

/* Pin-Buttons dezenter */
:deep(.btn-pin) {
  opacity: 0.35;
  transition: opacity 0.15s;
}
:deep(.btn-pin:hover) {
  opacity: 1;
}

/* Lemma-Container: kein exzessiver Scroll */
:deep(.reader__analysis-vocab__body__lemma-container) {
  max-height: none;
  overflow-y: visible;
}
</style>
